// src/cn/pages/Training/TrainingPageCN.jsx
import { useCallback, useMemo, useRef, useState } from "react";
import { TrainingAI } from "../../../lib/training";

function CopyButton({ text, className = "", label = "复制" }) {
  const onCopy = useCallback(async () => {
    if (!text) return;

    const msg = (typeof document !== "undefined" && document.documentElement?.lang) === "zh-CN"
      ? "已复制到剪贴板。"
      : "클립보드에 복사되었습니다.";

    try {
      await navigator.clipboard.writeText(text);
      alert(msg);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      alert(msg);
    }
  }, [text]);

  return (
    <button type="button" onClick={onCopy} className={className}>
      {label}
    </button>
  );
}

export default function TrainingPageCN() {
  // ===== 1) AI 상담 예행연습 (거절유형 스크립트) =====
  const [objection, setObjection] = useState("price");
  const [scriptDraft, setScriptDraft] = useState("");
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptError, setScriptError] = useState("");

  // ===== 2) 코칭 분석 =====
  const [coachingInput, setCoachingInput] = useState("");
  const [coachingDraft, setCoachingDraft] = useState("");
  const [coachingLoading, setCoachingLoading] = useState(false);
  const [coachingError, setCoachingError] = useState("");

  // ===== 3) 환자 시뮬레이터 =====
  const [persona, setPersona] = useState("price-sensitive");
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [simBusy, setSimBusy] = useState(false);
  const [report, setReport] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reportError, setReportError] = useState("");

  const busy = useMemo(() => scriptLoading || coachingLoading || simBusy || reportBusy, [scriptLoading, coachingLoading, simBusy, reportBusy]);

  // ===== Handlers =====
  const onGenerateScript = useCallback(async () => {
    setScriptLoading(true);
    setScriptError("");
    setScriptDraft("");
    try {
      const res = await TrainingAI.generateScript({ objectionKey: objection });
      setScriptDraft((res?.answer || "").trim());
    } catch (e) {
      setScriptError(e?.message || (getLang() === "zh-CN" ? "生成失败" : "생성 실패"));
    } finally {
      setScriptLoading(false);
    }
  }, [objection]);

  const onAnalyze = useCallback(async () => {
    if (!coachingInput.trim()) {
      alert("请输入要分析的咨询脚本。");
      return;
    }
    setCoachingLoading(true);
    setCoachingError("");
    setCoachingDraft("");
    try {
      const res = await TrainingAI.coaching({ script: coachingInput.trim() });
      setCoachingDraft((res?.answer || "").trim());
    } catch (e) {
      setCoachingError(e?.message || "分析失败");
    } finally {
      setCoachingLoading(false);
    }
  }, [coachingInput]);

  const historyRef = useRef([]);

  const startSimulation = useCallback(async () => {
    setSimBusy(true);
    setReport("");
    setReportError("");
    setChat([]);
    historyRef.current = [];
    try {
      const res = await TrainingAI.simFirst({ persona });
      const first = (res?.answer || "").trim();
      if (first) {
        setChat([{ role: "patient", text: first }]);
        historyRef.current.push({ role: "patient", text: first });
      }
    } catch (e) {
      alert(e?.message || "模拟开始失败");
    } finally {
      setSimBusy(false);
    }
  }, [persona]);

  const sendChat = useCallback(async () => {
    const t = chatInput.trim();
    if (!t || simBusy) return;
    setChatInput("");
    const next = [...chat, { role: "user", text: t }];
    setChat(next);
    historyRef.current.push({ role: "user", text: t });

    setSimBusy(true);
    try {
      const res = await TrainingAI.simChat({
        persona,
        history: historyRef.current,
      });
      const reply = (res?.answer || "").trim();
      if (reply) {
        setChat((prev) => [...prev, { role: "patient", text: reply }]);
        historyRef.current.push({ role: "patient", text: reply });
      }
    } catch (e) {
      alert(e?.message || "生成回复失败");
    } finally {
      setSimBusy(false);
    }
  }, [chatInput, chat, persona, simBusy]);

  const onEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      sendChat();
    }
  };

  const endSimulation = useCallback(async () => {
    setReportBusy(true);
    setReportError("");
    setReport("");
    try {
      const res = await TrainingAI.simReport({
        persona,
        history: historyRef.current,
      });
      setReport((res?.answer || "").trim());
    } catch (e) {
      setReportError(e?.message || "报告生成失败");
    } finally {
      setReportBusy(false);
    }
  }, [persona]);

  function getLang() {
    return (typeof document !== "undefined" && document.documentElement?.lang) || "ko";
  }

  // ===== Render =====
  return (
    <section className="training">
      <div id="content_title">
        <h2>AI MaC 咨询（训练）</h2>
        <p>面向咨询团队能力提升的 AI 训练工具</p>
      </div>

      <div className="content">
        {/* 1) 상담 예행연습 */}
        <div className="section borderType">
          <h3 className="sectionTitle">与 AI 一起进行咨询预演练习</h3>

          <div>
            <label className="inputTitle">选择患者拒绝类型</label>
            <select className=" useform" value={objection} onChange={(e) => setObjection(e.target.value)}>
              <option value="price">“价格太贵了。”（价格-价值不匹配）</option>
              <option value="decision">“我得和家人商量一下。”（决策压力）</option>
              <option value="trust">“不知道会不会有效。”（信任不足）</option>
              <option value="process">“我先考虑一下再联系你。”（流程摩擦）</option>
            </select>
          </div>

          <div>
            <button className="submitbtn" onClick={onGenerateScript} disabled={scriptLoading || busy}>
              {scriptLoading ? "生成中.." : "生成 AI 定制脚本"}
            </button>
          </div>

          {(scriptDraft || scriptError) && (
            <div className="trf-box">
              <div className="trf-box-head">AI 生成脚本</div>
              <div className="trf-box-body">
                <div className="trf-markdown">{scriptError ? `错误: ${scriptError}` : scriptDraft}</div>
                <div>
                  <CopyButton text={scriptDraft} label="复制" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 2) 코칭 분석 */}
        <div className="section borderType">
          <h3 className="sectionTitle">实战！AI 咨询辅导</h3>

          <div>
            <label className="inputTitle">输入要分析的咨询脚本</label>
            <textarea
              rows={8}
              className=" useform"
              placeholder="请粘贴咨询主管与患者的对话内容。"
              value={coachingInput}
              onChange={(e) => setCoachingInput(e.target.value)}
            />
          </div>

          <div>
            <button className="submitbtn" onClick={onAnalyze} disabled={coachingLoading || busy}>
              {coachingLoading ? "分析中.." : "请求分析我的脚本"}
            </button>
          </div>

          {(coachingDraft || coachingError) && (
            <div className="trf-box ">
              <div className="trf-box-head">AI 辅导反馈</div>
              <div className="trf-box-body">
                <div className="trf-markdown">{coachingError ? `错误: ${coachingError}` : coachingDraft}</div>
                <div>
                  <CopyButton text={coachingDraft} label="复制" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 3) 환자 시뮬레이터 */}
        <div className="section borderType">
          <h3 className="sectionTitle">实战！AI 患者模拟器</h3>

          <div>
            <label className="inputTitle">选择要咨询的患者人设</label>
            <select className=" useform" value={persona} onChange={(e) => setPersona(e.target.value)}>
              <option value="price-sensitive">价格敏感型大学生</option>
              <option value="skeptical-worker">怀疑效果型上班族</option>
              <option value="indecisive-homemaker">犹豫不决型主妇</option>
            </select>
          </div>

          <div>
            <button className="submitbtn" onClick={startSimulation} disabled={simBusy || busy}>
              {simBusy ? "启动中.." : "开始模拟"}
            </button>
          </div>

          {/* 채팅 UI */}
          {chat.length > 0 && (
            <div className="trf-box">
              <div className="trf-box-head">模拟对话</div>
              <div className="trf-box-body">
                <div className="chatbox">
                  <div className="chatlog">
                    {chat.map((m, i) => (
                      <div key={i} className={`bubble ${m.role === "user" ? "from-user" : "from-patient"}`}>
                        <div className="sim-text">{m.text}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="chatInput">
                  <input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={onEnter}
                    placeholder="请输入要发给患者的话术"
                  />
                  <button onClick={sendChat} disabled={simBusy || busy}>
                    发送
                  </button>
                </div>

                <div className="ahctActions">
                  <button onClick={endSimulation} disabled={reportBusy || busy}>
                    {reportBusy ? "报告生成中.." : "结束模拟并生成报告"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {(report || reportError) && (
            <div className="trf-box ">
              <div className="trf-box-head">AI 模拟报告</div>
              <div className="trf-box-body">
                <pre>{reportError ? `错误: ${reportError}` : report}</pre>
                <div>
                  <CopyButton text={report} label="复制" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
